import { useMutation, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Fragment } from 'react';

import { EditParkAndRideFacilityForm } from '@/components/management/form/EditParkAndRideFacilityForm';
import { FormGridLayout } from '@/components/management/form/formHelpers/FormGridLayout';
import { AdministrationBody } from '@/components/management/layout/AdministrationBody';
import { AdministrationHeader } from '@/components/management/layout/AdministrationHeader';
import { useIsAdmin } from '@/hooks/useIsAdmin';
import { useScrollSpy } from '@/hooks/useScrollSpy';
import {
  backendApiOrganizationsFindAllOptions,
  backendApiParkAndRideFacilitiesFindOneOptions,
  backendApiParkAndRideFacilitiesRemoveMutation,
  backendApiParkAndRideFacilitiesUpdateMutation,
} from '@/modules/api/backend/@tanstack/react-query.gen';
import { useConfirmStore } from '@/stores/useConfirmStore';
import { useSnackbarStore } from '@/stores/useSnackbarStore';
import { FeatureType } from '@/types/FeatureType';

export const Route = createFileRoute(
  '/_authenticated/management/stations/$stationId/park-and-ride-facilities/$parkAndRideFacilityId/',
)({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: RouteComponent,
  beforeLoad: () => ({ breadcrumb: null }),
});

function RouteComponent() {
  const navigate = Route.useNavigate();
  const { parkAndRideFacilityId } = Route.useParams();

  const { data: facilityData } = useSuspenseQuery(
    backendApiParkAndRideFacilitiesFindOneOptions({
      path: { parkAndRideFacilityId },
      query: { station: true },
    }),
  );

  const { data: organizations } = useSuspenseQuery(backendApiOrganizationsFindAllOptions());

  const { openSnackbar } = useSnackbarStore((state) => state.actions);
  const { openConfirm } = useConfirmStore((state) => state.actions);

  const { mutateAsync: mutateAsyncUpdate } = useMutation({
    ...backendApiParkAndRideFacilitiesUpdateMutation(),
    onSuccess: () => openSnackbar({ color: 'success', message: 'Die Änderungen wurden erfolgreich gespeichert!' }),
    onError: () => openSnackbar({ color: 'error', message: 'Fehler beim Speichern.' }),
  });

  const { mutateAsync: mutateAsyncDelete } = useMutation({
    ...backendApiParkAndRideFacilitiesRemoveMutation(),
    meta: {
      invalidateQueries: false,
    },
    onSuccess: () => {
      openSnackbar({ color: 'success', message: 'Die P+R-Anlage wurde erfolgreich gelöscht!' });
      navigate({
        to: '/management/stations/$stationId',
        replace: true,
      });
    },
    onError: () => openSnackbar({ color: 'error', message: 'Fehler beim Löschen der P+R-Anlage.' }),
  });

  const tabs = [
    'P+R-Anlage',
    'Status',
    'Verortung',
    'Details',
    'Stellplätze & Kapazität',
    'Auslastung',
    'Nicht-P+R-Nutzung',
    'Ausstattung',
    'Bewirtschaftung',
    'Zufahrt',
    'Fläche',
  ];
  const { setRef, scrollTo, activeIndex } = useScrollSpy<HTMLDivElement>(tabs.length);

  const isAdmin = useIsAdmin();

  return (
    <Fragment>
      <AdministrationHeader
        formId="parkingFacilityForm"
        label="P+R Anlage Bearbeiten"
        links={[
          {
            name: 'Bearbeitungsverlauf',
            options: { to: '/management/stations/$stationId/park-and-ride-facilities/$parkAndRideFacilityId/audits' },
            icon: 'CLOCK',
          },
          {
            name: 'Auf Karte anzeigen',
            options: {
              to: '/',
              search: { type: FeatureType.PARK_AND_RIDE_FACILITY, id: parkAndRideFacilityId },
            },
            icon: 'MAP_PIN',
          },
        ]}
        onDelete={async () => {
          const confirmed = await openConfirm({
            title: 'P+R-Anlage löschen',
            message: 'Sind Sie sicher, dass Sie diese P+R-Anlage unwiderruflich löschen möchten?',
            confirmText: 'Löschen',
            cancelText: 'Abbrechen',
          });

          if (confirmed) {
            await mutateAsyncDelete({
              path: { parkAndRideFacilityId },
            });
          }
        }}
      />
      <AdministrationBody>
        <FormGridLayout activeTab={activeIndex} onTabChange={scrollTo} tabs={tabs}>
          <EditParkAndRideFacilityForm
            disabled={!isAdmin}
            initialValues={facilityData}
            organizations={organizations}
            setRef={setRef}
            tabs={tabs}
            onSubmit={async (values) => {
              await mutateAsyncUpdate({ path: { parkAndRideFacilityId }, body: values });
            }}
          />
        </FormGridLayout>
      </AdministrationBody>
    </Fragment>
  );
}
